import React, { JSX, useState } from 'react';
import {
  IconBuildingBridge2,
  IconChevronDown,
  IconChevronUp,
  IconCircleDashed,
  IconUmbrella,
} from '@tabler/icons-react';
import { Collapse, Switch, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { CatchmentForm } from '@/components/catchmentform/CatchmentForm';
import { useMapContext } from '@/contexts/MapContext';
import { useMapFilterContext } from '@/contexts/MapFilterContext';
import { DropdownContent } from './dropdowncontent';
import { SudsFilter } from './sudsfilter';
import styles from './layersandfilters.module.css';

interface LayerItemProps {
  icon: JSX.Element;
  label: string;
  switchState?: boolean;
  onSwitchToggle?: (checked: boolean) => void;
  children?: React.ReactNode;
}

function LayerItemWithSwitch({
  icon,
  label,
  switchState,
  onSwitchToggle,
  children,
}: LayerItemProps) {
  const [opened, { toggle }] = useDisclosure(false);

  return (
    <>
      <li className={styles.keyItem}>
        <div className={styles.iconAndText}>
          <span className={styles.icon}>{icon}</span>
          <Text className={styles.layersText}>{label}</Text>
        </div>
        <div className={styles.switchAndDropdown}>
          <Switch
            className={styles.switch}
            checked={switchState}
            onChange={(event) => onSwitchToggle && onSwitchToggle(event.currentTarget.checked)}
          />
          <UnstyledButton className={styles.icon} onClick={toggle}>
            {opened ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
          </UnstyledButton>
        </div>
      </li>

      <Collapse
        in={opened}
        transitionDuration={200}
        transitionTimingFunction="linear"
        pt={8}
        pb={8}
      >
        {children}
      </Collapse>
    </>
  );
}

export default function LayersAndFilters() {
  const {
    showCsoMarkers,
    showSudsMarkers,
    showAreaBoundary,
    setShowCsoMarkers,
    setShowSudsMarkers,
    setShowAreaBoundary,
  } = useMapFilterContext();
  const { catchment, isCatchmentLoading, setCatchment } = useMapContext();
  const [areAllChecked, setAreAllChecked] = useState(true);

  const handleSwitchToggle = (checked: boolean) => {
    setAreAllChecked(checked);
    setShowCsoMarkers(checked);
  };

  const networkItems = [
    {
      label: 'CSO Locations',
      checked: showCsoMarkers,
      onChange: (checked: boolean) => setShowCsoMarkers(checked),
    },
  ];

  return (
    <div>
      <ul className={styles.keyItems}>
        {/* River Basin Districts */}
        <LayerItemWithSwitch
          icon={<IconCircleDashed size={16} />}
          label="Catchment Area"
          switchState={showAreaBoundary}
          onSwitchToggle={setShowAreaBoundary}
        >
          <CatchmentForm
            isLoading={isCatchmentLoading}
            onSubmit={setCatchment}
            isInitialSelection={false}
            initialValues={catchment}
          />
        </LayerItemWithSwitch>

        <hr className={styles.divider} />

        {/* SuDS */}
        <LayerItemWithSwitch
          icon={<IconUmbrella size={16} />}
          label="SuDS"
          switchState={showSudsMarkers}
          onSwitchToggle={setShowSudsMarkers}
        >
          <SudsFilter />
        </LayerItemWithSwitch>

        <hr className={styles.divider} />

        {/* Network / CSOs */}
        <LayerItemWithSwitch
          icon={<IconBuildingBridge2 size={16} />}
          label="Network"
          switchState={areAllChecked}
          onSwitchToggle={handleSwitchToggle}
        >
          <DropdownContent items={networkItems} />
        </LayerItemWithSwitch>
      </ul>
    </div>
  );
}
